/**
 * TouchEventDelegation - Singleton class for managing passive touch event delegation
 * Prevents memory leaks by properly cleaning up listeners on Turbo navigation
 */
class TouchEventDelegation {
    constructor() {
        this.grid = null;
        this.touchStartHandler = null;
        this.touchEndHandler = null;
        this.turboLoadHandler = null;
        this.turboFrameLoadHandler = null;
        this.turboBeforeCacheHandler = null;
        this.domContentLoadedHandler = null;
        this.isSetup = false;
        this._initialized = false;
    }

    /**
     * Find the tile controller for an element
     */
    findTileController(element) {
        const tileElement = element.closest('[data-controller*="tile"]');
        if (!tileElement || !window.Stimulus) return null;
        
        return window.Stimulus.application?.getControllerForElementAndIdentifier(tileElement, 'tile') || null;
    }

    /**
     * Create synthetic event for delegation
     */
    createSyntheticEvent(originalEvent, type, controller) {
        return {
            type,
            target: originalEvent.target,
            currentTarget: controller.element,
            touches: originalEvent.touches,
            changedTouches: originalEvent.changedTouches,
            preventDefault: () => {}, // No-op for passive listeners
            isTrusted: originalEvent.isTrusted
        };
    }

    /**
     * Set up passive touch event listeners on the grid
     */
    setup() {
        // Clean up any existing setup first
        this.cleanup();

        const grid = document.querySelector('#grid-panel .grid[data-queue-mode]') || document.querySelector('.grid[data-queue-mode]');
        if (!grid) return;

        this.grid = grid;

        // Create bound handlers
        this.touchStartHandler = (e) => {
            const controller = this.findTileController(e.target);
            if (controller) {
                controller.startMining(this.createSyntheticEvent(e, 'touchstart', controller));
            }
        };

        this.touchEndHandler = (e) => {
            const controller = this.findTileController(e.target);
            if (controller) {
                controller.startMining(this.createSyntheticEvent(e, 'touchend', controller));
            }
        };

        // Add passive listeners
        grid.addEventListener('touchstart', this.touchStartHandler, { passive: true });
        grid.addEventListener('touchend', this.touchEndHandler, { passive: true });

        this.isSetup = true;
    }

    /**
     * Clean up event listeners
     */
    cleanup() {
        if (this.grid && this.touchStartHandler) {
            this.grid.removeEventListener('touchstart', this.touchStartHandler);
        }
        if (this.grid && this.touchEndHandler) {
            this.grid.removeEventListener('touchend', this.touchEndHandler);
        }

        this.grid = null;
        this.touchStartHandler = null;
        this.touchEndHandler = null;
        this.isSetup = false;
    }

    /**
     * Initialize and set up Turbo event listeners
     */
    initialize() {
        // Only initialize once
        if (this._initialized) return;
        this._initialized = true;

        // Set up initial delegation
        const setup = () => this.setup();
        
        if (document.readyState === 'loading') {
            this.domContentLoadedHandler = setup;
            document.addEventListener('DOMContentLoaded', this.domContentLoadedHandler, { once: true });
        } else {
            setup();
        }

        // Set up Turbo navigation listeners
        this.turboLoadHandler = () => this.setup();
        this.turboFrameLoadHandler = (e) => {
            if (e.target.id === 'grid-panel') {
                this.setup();
            }
        };
        this.turboBeforeCacheHandler = () => this.cleanup();

        document.addEventListener('turbo:load', this.turboLoadHandler);
        document.addEventListener('turbo:frame-load', this.turboFrameLoadHandler);
        document.addEventListener('turbo:before-cache', this.turboBeforeCacheHandler);
    }

    /**
     * Clean up all listeners including Turbo listeners
     */
    destroy() {
        this.cleanup();

        if (this.turboLoadHandler) {
            document.removeEventListener('turbo:load', this.turboLoadHandler);
            this.turboLoadHandler = null;
        }

        if (this.turboFrameLoadHandler) {
            document.removeEventListener('turbo:frame-load', this.turboFrameLoadHandler);
            this.turboFrameLoadHandler = null;
        }

        if (this.turboBeforeCacheHandler) {
            document.removeEventListener('turbo:before-cache', this.turboBeforeCacheHandler);
            this.turboBeforeCacheHandler = null;
        }

        if (this.domContentLoadedHandler) {
            document.removeEventListener('DOMContentLoaded', this.domContentLoadedHandler);
            this.domContentLoadedHandler = null;
        }

        this._initialized = false;
    }
}

// Export singleton instance
export default new TouchEventDelegation();

